import * as React from 'react'
import * as Sentry from '@sentry/react'
import { FilesError } from 'react-files'
import { Export, PersistDB } from 'slate-persistence'
import store from '../../../../../store'
import { addAlert } from '../../../../../store/actions/alerts'
import { saveAsFile } from '../../../../../helpers'
import ClearDatabaseDialog from '../ClearDatabaseDialog'
import Spinner from '../../../../../components/Spinner'
import Button from '../../../../../components/ui/Button'
import Dialog from '../../../../../components/ui/Dialog'
import FileUploader from '../../../../../containers/FilesUploader'

type HelpdeskToolsState = {
  isExportingDatabase: boolean
  isImportingDatabase: boolean
  showImportDatabase: boolean
  files: File[]
  uploadedDatabase: Export | undefined
  showClearDatabase: boolean
}

class HelpdeskTools extends React.Component {
  state: HelpdeskToolsState = {
    isExportingDatabase: false,
    isImportingDatabase: false,
    showImportDatabase: false,
    files: [],
    uploadedDatabase: undefined,
    showClearDatabase: false,
  }

  private exportDatabase = async () => {
    try {
      this.setState({ isExportingDatabase: true })
      const db = await PersistDB.open()
      const data = await db.export()
      const filename = `IndexedDB-${new Date().toISOString()}.json`
      saveAsFile(filename, JSON.stringify(data, null, 2), 'application/json')
    } catch (e) {
      Sentry.captureException(e)
      store.dispatch(addAlert('error', 'helpdesk-export-error', { details: e.toString() }))
    }
    this.setState({ isExportingDatabase: false })
  }

  private showImportDatabase = () => {
    this.setState({ showImportDatabase: true })
  }

  private closeImportDatabase = () => {
    this.setState({
      isImportingDatabase: false,
      showImportDatabase: false,
      files: [],
      uploadedDatabase: undefined,
    })
  }

  private importDatabase = async () => {
    this.setState({ isImportingDatabase: true })

    const { uploadedDatabase } = this.state
    if (!uploadedDatabase) {
      store.dispatch(addAlert('error', 'helpdesk-import-error-uploaded-database'))
      this.closeImportDatabase()
      return
    }

    try {
      const db = await PersistDB.open()
      await db.import(uploadedDatabase)
      store.dispatch(addAlert('success', 'helpdesk-import-success'))
    } catch (e) {
      console.error(e)
      store.dispatch(addAlert('error', 'helpdesk-import-error', { details: e.toString() }))
    }

    this.closeImportDatabase()
  }

  private showClearDatabase = () => {
    this.setState({ showClearDatabase: true })
  }

  private closeClearDatabase = () => {
    this.setState({ showClearDatabase: false })
  }

  filesRef: React.RefObject<FileUploader> = React.createRef()

  public render() {
    const {
      isExportingDatabase,
      isImportingDatabase,
      showImportDatabase,
      files,
      showClearDatabase,
    } = this.state

    return (
      <div className="helpdesk__tools">
        <Button
          l10nId={
            isExportingDatabase
              ? "helpdesk-export-local-database-loading"
              : "helpdesk-export-local-database"
          }
          clickHandler={this.exportDatabase}
          isDisabled={isExportingDatabase}
        />
        <Button l10nId="helpdesk-import-local-database" clickHandler={this.showImportDatabase}>
          Import local database
        </Button>
        <Button l10nId="helpdesk-clear-local-database-button" clickHandler={this.showClearDatabase}>
          Clear local database for specific drafts
        </Button>
        {
          showClearDatabase
            ?
            <ClearDatabaseDialog
              onClose={this.closeClearDatabase}
            />
            : null
        }
        {
          showImportDatabase ?
            <Dialog
              size="medium"
              l10nId="helpdesk-import-database-title"
              placeholder="Replace whole database or select drafts to replace"
              onClose={this.closeImportDatabase}
              showCloseButton={false}
            >
              {
                isImportingDatabase ?
                  <Spinner />
                  :
                  <div className="helpdesk__dialog-content">
                    <FileUploader
                      onFilesChange={this.onFilesChange}
                      onFilesError={this.onFilesError}
                      multiple={false}
                      accepts={['.json']}
                      optional={false}
                      ref={this.filesRef}
                    />
                    <div className="dialog__buttons">
                      <Button l10nId="helpdesk-import-cancel" clickHandler={this.closeImportDatabase}>
                        Cancel
                      </Button>
                      <Button
                        l10nId="helpdesk-import-confirm"
                        clickHandler={this.importDatabase}
                        isDisabled={!files.length}
                      >
                        Import database
                      </Button>
                    </div>
                  </div>
              }
            </Dialog>
            : null
        }
      </div>
    )
  }

  private onFilesChange = async (files: File[]) => {
    let uploadedDatabase
    if (files.length) {
      uploadedDatabase = JSON.parse(await new Response(files[0]).text())
      if (
        !uploadedDatabase.database ||
        typeof uploadedDatabase.database.name !== 'string' ||
        typeof uploadedDatabase.database.version !== 'number' ||
        !uploadedDatabase.remove ||
        !uploadedDatabase.insert
      ) {
        store.dispatch(addAlert('error', 'helpdesk-import-invalid-file'))
        this.filesRef.current!.filesRemoveOne(files[0])
        this.setState({ files: [], uploadedDatabase: undefined })
        return
      }
    }
    this.setState({ files, uploadedDatabase })
  }

  private onFilesError = (error: FilesError, _: File) => {
    store.dispatch(addAlert('error', 'file-upload-error', { code: error.code }))
  }
}

export default HelpdeskTools
